import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import styled from "@emotion/styled";
import { E164Number } from "../../../../../api/src/types";
import PhoneField from "../../../components/forms/PhoneInputField";
import { QuestionWithRelations } from "../../../../../api/src/types";
import { validatePhoneWithRestriction } from "../utils/questionnaire";
import { useGetCurrentUserQuery } from "../../../api/users";
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from "../../../api/answers";
import { InputWrapper, IconContainer, ErrorWrapper } from "./SharedFormStyles";
import Loader from "../../../components/icons/Loader";
import GreenTick from "../../../components/icons/GreenTick";
import { IconSize } from "../../../components/theme";
import useIsMobile from "../../../hooks/useIsMobile";

export const PhoneFieldContainer = styled.div`
  flex: 1;
`;

interface PhoneInputFieldProps {
  question: QuestionWithRelations;
}

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({ question }) => {
  const isMobile = useIsMobile();
  const [phoneInput, setPhoneInput] = useState<string | null>(
    question.answers[0]?.valueTyped ?? null,
  );
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const { data: user } = useGetCurrentUserQuery();
  const [
    createAnswer,
    {
      isLoading: isCreating,
      isError: isCreateError,
      isSuccess: isCreateSuccess,
      reset: resetCreateAnswer,
    },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    {
      isLoading: isUpdating,
      isError: isUpdateError,
      isSuccess: isUpdateSuccess,
      reset: resetUpdateAnswer,
    },
  ] = useUpdateAnswerMutation();

  const hasAnswer = question.answers.length > 0;

  const save = useDebouncedCallback((value: string | null) => {
    if (!user) return;

    const payload = {
      questionId: question.id,
      userId: user.id,
      valueTyped: value,
    };

    if (!hasAnswer) void createAnswer(payload);
    else void updateAnswer(payload);
  }, 1000);

  const handlePhoneChange = (value: E164Number | undefined) => {
    resetCreateAnswer();
    resetUpdateAnswer();

    setPhoneInput(value ?? "");
    try {
      const error = validatePhoneWithRestriction(value ?? "", question);
      setPhoneError(error);
      if (value === undefined || value.length === 0) {
        save(null);
        return;
      } else if (!error) {
        save(value);
        return;
      }
    } catch (err) {
      save(null);
      console.error("Phone validation error:", err);
      setPhoneError("Error processing phone number");
    }
  };

  const isLoading = isCreating || isUpdating;
  const hasError = isCreateError || isUpdateError;
  const isSuccess =
    (isCreateSuccess || isUpdateSuccess) &&
    !hasError &&
    !isLoading &&
    !phoneError &&
    phoneInput !== "";

  return (
    <>
      <InputWrapper>
        <PhoneFieldContainer>
          <PhoneField
            className="phone-input"
            value={phoneInput ?? ""}
            handlePhoneChange={handlePhoneChange}
            placeholder="Enter phone number"
            errorMessage={phoneError}
            required={question.isRequired}
          />
        </PhoneFieldContainer>
        {isLoading && (
          <IconContainer>
            <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
          </IconContainer>
        )}
        {isSuccess && (
          <IconContainer>
            <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
          </IconContainer>
        )}
      </InputWrapper>
      {hasError && (
        <ErrorWrapper>
          Error saving answer, please refresh the page and try again
        </ErrorWrapper>
      )}
    </>
  );
};

export default PhoneInputField;
